import mediaService from './media.service'

import {
  ICustomIconFile,
  IMediaBase,
  IMediaFiles,
  IMediaLinkUploaded,
  IMediaLinksUploaded,
  IMediaRespDto,
  IMediaFile,
  IUploadedMediaResp,
} from 'src/types'

class MediaEntityUploadService {
  // * Upload

  async uploadMedia(
    media: IMediaFiles,
    withoutCompression: boolean = false,
  ): Promise<IMediaBase[]> {
    const promises = this.deriveMediaPromises(media, withoutCompression)

    const links = await Promise.all(promises)

    return this.createMedia(links, media)
  }

  async uploadIcon(file: File | null, icon: string | null) {
    let result: IUploadedMediaResp | null | void = null

    if (file) {
      result = await mediaService.uploadFileWithCompression(file)
    } else if (icon && this.isUploadedMediaLink(icon)) {
      result = await mediaService.duplicateFileWithCompression(icon)
    } else if (icon && this.isStockMediaLink(icon)) {
      result = await mediaService.uploadFileFromUrl(icon)
    }

    return result
      ? [
          result.link,
          result.link_small || null,
          result.link_medium || null,
          result?.link_large || null,
        ]
      : null
  }

  private deriveMediaPromises(media: IMediaFiles, withoutCompression: boolean = false) {
    return media.reduce<IMediaLinksUploaded | Promise<IMediaLinkUploaded>[]>((acc: any, media) => {
      const linkPromise = this.deriveMediaLinkPromise(media, withoutCompression)
      return linkPromise ? [...acc, linkPromise] : acc
    }, [])
  }

  private async deriveMediaLinkPromise(media: IMediaFile, withoutCompression: boolean = false) {
    let resp = null

    let options = {}
    if (withoutCompression) {
      options = { compression: [] }
    }

    if (media.file) {
      const uploaded = await mediaService.uploadFileWithCompression(media.file, options)

      resp = {
        link: uploaded?.link,
        link_small: uploaded?.link_small,
        link_medium: uploaded?.link_medium,
        link_large: uploaded?.link_large,
        dimensions: uploaded?.dimensions,
      }
    } else if (media.link) {
      let uploaded = null

      if (
        media.file === null &&
        media.link &&
        media.link_medium &&
        media.link_small &&
        this.isStockPexelVideoLink(media.link, media.link_medium, media.link_small)
      ) {
        uploaded = await mediaService.uploadPexelsVideosFromUrl(
          media.link,
          media.link_medium,
          media.link_small,
        )

        return {
          link: uploaded?.link || media.link,
          link_small: null,
          link_medium: null,
          link_large: null,
          dimensions: uploaded?.dimensions,
        }
      }

      if (this.isStockMediaLink(media.link)) {
        uploaded = await mediaService.uploadFileFromUrl(media.link)
      }

      if (this.isUploadedMediaLink(media.link)) {
        uploaded = await mediaService.duplicateFileWithCompression(media.link)
      }

      resp = {
        link: uploaded?.link || media.link,
        link_small: uploaded?.link_small || media.link_small,
        link_medium: uploaded?.link_medium || media.link_medium,
        link_large: uploaded?.link_large || media.link_large,
        dimensions: uploaded?.dimensions,
      }
    }

    return resp
  }

  private createMedia(uploadedData: IMediaLinksUploaded, media: IMediaFiles) {
    return uploadedData.map((data, index) => {
      return {
        link: data.link,
        link_small: data.link_small,
        link_medium: data.link_medium,
        link_large: data?.link_large,
        type: media[index].type || 'url',
        title: media[index].title,
        main_image: media[index].main_image,
        show_item: media[index].show_item,
        dimensions: data.dimensions,
      }
    })
  }

  // * Update
  async updateMedia(oldMedia: IMediaRespDto, newMedia: IMediaFiles) {
    // Remove from AWS deleted media
    const removedPromises = oldMedia.reduce<Promise<void>[]>(
      (acc, { link, link_large, link_medium, link_small }) =>
        newMedia.some((media) => media.link === link)
          ? acc
          : [
              ...acc,
              link_small || link_medium || link_large
                ? mediaService.removeFileWithCompression(link)
                : mediaService.removeFile(link),
            ],
      [],
    )
    await Promise.all(removedPromises)

    // Upload to AWS new media
    const uploadedPromises = newMedia.reduce<Promise<Partial<IMediaBase>>[]>(
      (acc, { file, ...media }) => {
        if (file) {
          return [
            ...acc,
            mediaService.uploadFileWithCompression(file).then((resp) => {
              return {
                type: media.type,
                title: media.title,
                link: resp?.link,
                link_small: resp?.link_small,
                link_medium: resp?.link_medium,
                link_large: resp?.link_large,
                main_image: media.main_image,
                show_item: media.show_item,
                dimensions: resp?.dimensions,
              }
            }),
          ]
        }

        if (
          file === null &&
          media.link &&
          media.link_medium &&
          media.link_small &&
          this.isStockPexelVideoLink(media.link, media.link_medium, media.link_small)
        ) {
          return [
            ...acc,
            mediaService
              .uploadPexelsVideosFromUrl(media.link, media.link_medium, media.link_small)
              .then((resp) => ({
                type: media.type,
                title: media.title,
                link: resp?.link,
                link_small: resp?.link_small,
                link_medium: resp?.link_medium,
                main_image: media.main_image,
                show_item: media.show_item,
                dimensions: resp?.dimensions,
              })),
          ]
        }

        if (file === null && media.link && this.isStockMediaLink(media.link)) {
          return [
            ...acc,
            mediaService.uploadFileFromUrl(media.link).then((resp) => ({
              type: media.type,
              title: media.title,
              link: resp?.link,
              link_small: resp?.link_small,
              link_medium: resp?.link_medium,
              main_image: media.main_image,
              show_item: media.show_item,
              dimensions: resp?.dimensions,
            })),
          ]
        }
        return [...acc, Promise.resolve(media)]
      },
      [],
    ) as Promise<IMediaBase>[]
    return Promise.all(uploadedPromises)
  }

  async updateIcon(
    oldImage: string | null,
    newImage: ICustomIconFile,
  ): Promise<(string | null)[] | null> {
    const promises = []

    if (
      oldImage &&
      (newImage.file || oldImage !== newImage.icon) &&
      (this.isUploadedMediaLink(oldImage) || this.isStockMediaLink(oldImage))
    ) {
      promises.push(mediaService.removeFileWithCompression(oldImage))
    }
    if (newImage.file) {
      promises.push(mediaService.uploadFileWithCompression(newImage.file))
    }
    if (newImage.icon && this.isStockMediaLink(newImage.icon)) {
      promises.push(mediaService.uploadFileFromUrl(newImage.icon))
    }

    if (promises.length) {
      const results = await Promise.all(promises)
      const result = results.at(-1) || null

      return result ? [result.link, result.link_small || null, result.link_medium || null] : null
    }

    return null
  }

  private isUploadedMediaLink(link: string) {
    return (
      link.includes('amazonaws.com') ||
      link.includes(process.env.REACT_APP_AWS_BUCKET_NAME || 'trackgood.io')
    )
  }

  private isStockMediaLink(link: string) {
    return link.includes('images.pexels')
  }

  private isStockPexelVideoLink(link: string, link_medium: string, link_small: string) {
    return (
      link.includes('videos.pexels') &&
      link_medium.includes('videos.pexels') &&
      link_small.includes('videos.pexels')
    )
  }

  private async uploadPexelsVideosFromUrl(videoUrl: string) {
    try {
      const videoResponse = await fetch(videoUrl)

      if (!videoResponse.ok) {
        throw new Error('Failed to download video')
      }

      const videoBlob = await videoResponse.blob()

      return videoBlob
    } catch (error) {
      console.error('Error uploading Pexels video:', error)
      return null
    }
  }
}

export const mediaEntityUploadService = new MediaEntityUploadService()

export default MediaEntityUploadService
